/**
 * The one place that talks to the issue tracker (Bugsnag).
 *
 * Started from main.ts before Angular, so a failed bootstrap is reported too.
 * Everything else goes through the `issueTracker` instance below:
 *
 *   notify(thrown)             the global error handler's reports
 *   reportHandled(error, what) failures a handler dealt with quietly
 *   leaveBreadcrumb(...)       the GraphQL link's one entry per operation
 *
 * Every event says
 *   what   class, severity, message and group from classify.ts, a `graphql`
 *          tab (operation, variables, code, reason, path) and a `test` tab in
 *          the phantom-testing flow
 *   who    the logged-in user, and an `account` tab (tenant, role, facility)
 *   where  the route with its ids replaced as the context (`/home/studies/:id`),
 *          the real path and ids in a `page` tab
 *
 * The trail before an error is compact: clicks, navigation, one entry per
 * GraphQL operation and the requests that failed. Console output is not
 * recorded: it carried whole request and response bodies (a 0.8 MB response
 * is most of the tracker's 1 MB event limit, and bodies are where passwords
 * were logged).
 */
import Bugsnag, {
  Breadcrumb,
  BreadcrumbType,
  BrowserConfig,
  Client,
  Event,
} from '@bugsnag/js'
import { redactSecretsInText, SECRET_KEY_PATTERN } from 'common/redact'
import { environment } from 'src/environments/environment'
import { version } from '../../../../package.json'
import { Classification, classify, HandledFailure } from './classify'
import { ErrorContext, getErrorContext } from './errorContext'

/** the logged-in user as the login response describes them */
export interface TrackedUser {
  id: number | string
  username?: string | null
  firstName?: string | null
  lastName?: string | null
  /** present for Microsoft logins */
  email?: string | null
  role?: string | null
  accountType?: string | null
  facility?: { id?: number | null; name?: string | null } | null
}

/** the part of the Bugsnag client this file uses; specs pass a fake */
export type TrackerClient = Pick<Client, 'notify' | 'leaveBreadcrumb'>

const MAX_VARIABLES_LENGTH = 2000

/** everything Bugsnag records on its own except console output ('log') */
const BREADCRUMB_TYPES: BreadcrumbType[] = [
  'error',
  'manual',
  'navigation',
  'process',
  'request',
  'state',
  'user',
]

/** `interior` from `interior.myaqs.com`; any other host as it is */
export function tenantOf(hostname: string): string {
  const match = /^(.+)\.myaqs\.com$/i.exec(hostname)
  return match ? match[1] : hostname
}

/** the route a path belongs to: `/home/studies/:id` for `/home/studies/2000014960` */
export function routePattern(pathname: string): string {
  return (
    pathname
      .split('/')
      .map((segment) => (/^\d+$/.test(segment) ? ':id' : segment))
      .join('/') || '/'
  )
}

/** the ids `routePattern` replaced, in order */
export function idsIn(pathname: string): string[] {
  return pathname.split('/').filter((segment) => /^\d+$/.test(segment))
}

/** "First Last (username)", or whichever parts exist */
export function displayName(user: TrackedUser): string | undefined {
  const name = [user.firstName, user.lastName]
    .filter((part) => !!part)
    .join(' ')
  if (name && user.username) {
    return `${name} (${user.username})`
  }
  return name || user.username || undefined
}

/** `value` as JSON of at most `maxLength` characters */
export function compactJson(value: unknown, maxLength: number): string {
  let json: string | undefined
  try {
    json = JSON.stringify(value)
  } catch (e) {
    return '[cannot be serialised]'
  }
  if (json === undefined) {
    return ''
  }
  return json.length > maxLength ? `${json.slice(0, maxLength - 1)}…` : json
}

/**
 * Redacts serialized secrets in a breadcrumb's text values. Bugsnag's
 * key-based `redactedKeys` cannot see inside strings such as a URL with a
 * token in it.
 */
export function scrubBreadcrumb(breadcrumb: Breadcrumb): void {
  const { metadata } = breadcrumb
  if (!metadata) return
  Object.keys(metadata).forEach((key) => {
    const value = metadata[key]
    if (typeof value === 'string') {
      metadata[key] = redactSecretsInText(value)
    }
  })
}

/**
 * Decides whether Bugsnag keeps a breadcrumb, and scrubs the ones it keeps.
 *
 * Dropped: requests that succeeded and say nothing about a later error. Image
 * downloads come by the dozen on a study page, and every GraphQL request
 * already has the entry the link leaves for it (operation name, variables,
 * duration). Requests that failed are always kept.
 */
export function keepBreadcrumb(breadcrumb: Breadcrumb): boolean {
  if (breadcrumb.type === 'request' && / succeeded$/.test(breadcrumb.message)) {
    // "<method> <url>", the method as the caller spelled it (the image
    // loader writes "get")
    const request = String(breadcrumb.metadata?.request ?? '')
    if (/^get \S*\/files\//i.test(request)) {
      return false
    }
    if (/^post \S*\/graphql(?:\?\S*)?$/i.test(request)) {
      return false
    }
  }
  scrubBreadcrumb(breadcrumb)
  return true
}

/** `values` without its undefined entries; undefined when nothing is left */
function defined(values: {
  [key: string]: unknown
}): { [key: string]: unknown } | undefined {
  const kept: { [key: string]: unknown } = {}
  Object.keys(values).forEach((key) => {
    if (values[key] !== undefined) {
      kept[key] = values[key]
    }
  })
  return Object.keys(kept).length > 0 ? kept : undefined
}

/** Writes what happened onto an event: class, severity, message, group and the tabs about the failure. */
export function applyClassification(
  event: Event,
  classification: Classification,
  context: ErrorContext,
): void {
  event.severity = classification.severity
  const [reported] = event.errors
  if (reported) {
    reported.errorClass = classification.errorClass
    reported.errorMessage = classification.message
  }
  if (classification.groupingHash) {
    event.groupingHash = classification.groupingHash
  }
  const tabs = classification.tabs || {}
  Object.keys(tabs).forEach((tab) => event.addMetadata(tab, tabs[tab]))

  const { operation, test } = context
  const { response, analysis } = classification
  if (operation || response?.code || response?.path) {
    const graphql = defined({
      operation: operation?.name,
      type: operation?.type,
      variables: operation
        ? compactJson(operation.variables, MAX_VARIABLES_LENGTH)
        : undefined,
      code: response?.code,
      reason: response?.reason,
      path: response?.path,
      status: response?.status,
    })
    if (graphql) {
      event.addMetadata('graphql', graphql)
    }
  }
  if (test || analysis) {
    const testTab = defined({
      study: test?.studyId,
      routine: test?.routineId,
      phantom: analysis?.phantom,
      'analyzer test id': analysis?.testId,
      reason: analysis?.reason,
      detail: analysis?.detail,
      'shown to the user': analysis?.message,
    })
    if (testTab) {
      event.addMetadata('test', testTab)
    }
  }
}

export class IssueTracker {
  private client: TrackerClient | undefined
  private user: () => TrackedUser | undefined = () => undefined

  /**
   * Start reporting. Once, from main.ts; later calls do nothing.
   *
   * @param create only for specs; the default starts Bugsnag
   */
  start(
    create: (config: BrowserConfig) => TrackerClient = (config) =>
      Bugsnag.start(config),
  ): void {
    if (this.client) {
      return
    }
    try {
      this.client = create({
        apiKey: environment.bugsnagApiKey,
        // release tracking: which build an event came from
        appVersion: version,
        // only production reports; keep dev sessions out of the production
        // stability numbers too
        releaseStage: environment.production ? 'production' : 'development',
        enabledReleaseStages: ['production'],
        redactedKeys: [SECRET_KEY_PATTERN],
        enabledBreadcrumbTypes: BREADCRUMB_TYPES,
        maxBreadcrumbs: 50,
        onBreadcrumb: (breadcrumb) => keepBreadcrumb(breadcrumb),
        // one parameter on purpose: Bugsnag treats a two-parameter callback
        // as asynchronous and waits for it
        onError: (event) => this.describeSession(event),
      })
    } catch (e) {
      // this runs before Angular: the app must start even if its tracker
      // cannot
      console.error('could not start the issue tracker', e)
    }
  }

  /** Where the tracker asks who is logged in, each time it reports (AuthService). */
  identifyWith(source: () => TrackedUser | undefined): void {
    this.user = source
  }

  /**
   * Report whatever was thrown, under the class, severity and group
   * classify() gives it. Never throws.
   */
  notify(thrown: unknown): void {
    // the tracker is only used in production
    if (!this.client || !environment.production) {
      return
    }
    try {
      const context = getErrorContext(thrown)
      const classification = classify(thrown, context)
      // Bugsnag reads name, message and stack from an Error; the class and
      // the message are set from the classification below either way
      const carrier =
        thrown instanceof Error ? thrown : new Error(classification.message)
      this.client.notify(carrier, (event) =>
        applyClassification(event, classification, context),
      )
    } catch (e) {
      // reporting must never become the user's problem
      console.error('could not report to the issue tracker', e)
    }
  }

  /**
   * Report a failure the caller has already dealt with (a default was used,
   * its own message was shown) and that would otherwise only reach the
   * console. Shows nothing to the user.
   *
   * @param operation what failed, as a short phrase: 'load the site timezone'.
   *   Events are grouped on it, so keep it constant (no ids or names in it).
   */
  reportHandled(error: unknown, operation: string): void {
    this.notify(new HandledFailure(operation, error))
  }

  /** Add an entry to the trail that is sent with the next report. Never throws. */
  leaveBreadcrumb(
    message: string,
    metadata: { [key: string]: unknown },
    type: BreadcrumbType = 'request',
  ): void {
    try {
      this.client?.leaveBreadcrumb(message, metadata, type)
    } catch (e) {
      // a lost trail entry is not worth an error
    }
  }

  /** Who and where, on every event (also the ones Bugsnag catches by itself). */
  private describeSession(event: Event): void {
    try {
      const { hostname, origin, pathname, search } = window.location
      const user = this.user()
      if (user) {
        event.setUser(
          String(user.id),
          user.email || undefined,
          displayName(user),
        )
      }
      event.addMetadata(
        'account',
        defined({
          tenant: tenantOf(hostname),
          role: user?.role || undefined,
          facility: user?.facility?.name || undefined,
          'account type': user?.accountType || undefined,
          'logged in': !!user,
        }),
      )
      event.context = routePattern(pathname)
      const path = redactSecretsInText(`${pathname}${search}`)
      event.addMetadata('page', defined({ path, ids: idsIn(pathname) }))
      // Bugsnag's own copy of the address is the whole location.href. Send it
      // without the fragment (a Microsoft login comes back with its ID token
      // there, and the app's routes do not use one) and with the same
      // redaction as the page tab.
      event.request = { ...event.request, url: `${origin}${path}` }
    } catch (e) {
      // an event without these is better than no event
    }
  }
}

/** the app's tracker; started in main.ts */
export const issueTracker = new IssueTracker()

/** issueTracker.reportHandled, for handlers that only need this */
export const reportHandled = (error: unknown, operation: string): void =>
  issueTracker.reportHandled(error, operation)
